import { Award, Users, TrendingUp, MapPin, Star } from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal } from "../components/Reveal";
import { AnimatedCounter } from "../components/AnimatedCounter";
import { CTABanner } from "../components/CTABanner";

const MILESTONES = [
{ value: 25, suffix: "+", label: "Years of Expertise" },
{ value: 50, suffix: "+", label: "Professionals On-Ground" },
{ value: 7, suffix: "", label: "Cities Covered" },
{ value: 500, suffix: "+", label: "Properties Managed" }];


const VALUES = [
{ icon: Award, title: "Integrity First", desc: "We operate with complete transparency. Every fee, every decision, every repair documented and disclosed. No surprises, ever." },
{ icon: TrendingUp, title: "Operational Excellence", desc: "Born from hospitality, we bring the same precision and standards of 5 star management to your property investment." },
{ icon: Users, title: "Client Partnership", desc: "You're not a file number. Our team builds long term relationships and treats your property as if it were our own." },
{ icon: Star, title: "Continuous Improvement", desc: "From manual processes to our upcoming technology platform\xA0 we constantly invest in becoming better for our clients." }];


export default function AboutPage() {
  return (
    <>
      <section className="relative overflow-hidden bg-stone-50 pt-40 pb-24" data-testid="about-hero">
        <div className="pointer-events-none absolute inset-0 -z-10 opacity-30" style={{ backgroundImage: `radial-gradient(circle, #0A254018 1px, transparent 1px)`, backgroundSize: "28px 28px" }} />
        <div className="container-luxury text-center">
          <Reveal><p className="eyebrow mb-5">About AssetCare365</p></Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-sans text-display-lg font-semibold text-navy-800 tracking-tight text-balance leading-tight mx-auto max-w-3xl">
              Built on Decades of <span className="font-display italic font-normal text-emerald-500">Hospitality Excellence</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-xl text-navy-600/70 max-w-2xl mx-auto text-balance leading-relaxed">AssetCare365 was built on a simple belief: property owners  especially those living abroad  deserve the same standard of care and transparency that 5 star hotels give their guests.

            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 bg-navy-800 noise-overlay" data-testid="stats-section">
        <div className="container-luxury">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            {MILESTONES.map((m, i) =>
            <Reveal key={m.label} delay={i * 0.1}>
                <div className="text-center">
                  <p className="font-sans text-4xl font-semibold text-white tracking-tight"><AnimatedCounter value={m.value} suffix={m.suffix} /></p>
                  <p className="mt-2 text-xs font-medium uppercase tracking-[0.16em] text-white/50">{m.label}</p>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <section className="py-32">
        <div className="container-luxury">
          <div className="grid gap-16 lg:grid-cols-2 lg:gap-24 items-center">
            <Reveal y={40}>
              <div className="relative h-[560px] rounded-2xl overflow-hidden shadow-luxury-lg">
                <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=900&q=85" alt="Modern office - AssetCare365 HQ" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-900/50 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6">
                  <div className="rounded-xl bg-white/95 backdrop-blur-sm px-5 py-3 inline-block">
                    <p className="text-xs font-semibold uppercase tracking-wider text-navy-800">Est. through 25+ Years of Hospitality</p>
                  </div>
                </div>
              </div>
            </Reveal>
            <div>
              <SectionHeading eyebrow="Our Story" title="From Hospitality" titleAccent="to Property Excellence" align="left" />
              <div className="mt-8 space-y-5 text-navy-600/75 leading-relaxed">
                <Reveal delay={0.1}><p>AssetCare365 is the property management arm of our parent company with over 25 years of distinguished experience in India's hospitality sector. Working alongside leading builders, developers, and hospitality giants, we built deep expertise in managing premium assets  with uncompromising standards of care, documentation, and operational discipline.</p></Reveal>
                <Reveal delay={0.2}><p>As India's NRI community grew and their need for a trustworthy, transparent on ground partner became clear, we made a natural evolution: bringing that same 5 star management philosophy to residential and commercial property ownership.</p></Reveal>
                <Reveal delay={0.3}><p>Today, AssetCare365 serves NRIs across 7 major cities and growing with a team of 50+ dedicated professionals who understand that for most NRIs, their Indian property is not just an investment, but a connection to home.</p></Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-32 bg-stone-50" data-testid="leadership-section">
        <div className="container-luxury">
          <SectionHeading eyebrow="Leadership" title="Meet the Team" titleAccent="Behind the Promise" subtitle="A leadership team with real on-ground expertise, decades of industry relationships, and a genuine commitment to transparency." />
          <div className="mt-20 grid gap-8 md:grid-cols-2">
            <Reveal delay={0.1}>
              <div className="rounded-2xl bg-white border border-navy-100 overflow-hidden shadow-sm hover:shadow-luxury transition-shadow duration-300">
                <div className="relative h-64 bg-gradient-to-br from-navy-700 to-navy-900 flex items-center justify-center">
                  <div className="h-24 w-24 rounded-full bg-gold-400/20 border-2 border-gold-400/40 flex items-center justify-center">
                    <span className="text-3xl font-display text-gold-300">VK</span>
                  </div>
                  <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-white to-transparent" />
                </div>
                <div className="p-7 pt-3">
                  <p className="eyebrow mb-2">Managing Director</p>
                  <h3 className="text-xl font-semibold text-navy-800 tracking-tight">Vinod kumar</h3>
                  <p className="mt-3 text-sm text-navy-600/70 leading-relaxed">With decades of experience working with India's leading builders and hospitality giants, our Managing Director brings deep market intelligence, trusted relationships, and uncompromising standards to every client engagement.</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {["25+ Years Experience", "Pan India Network", "Hospitality Expert"].map((tag) =>
                    <span key={tag} className="rounded-full bg-navy-50 px-3 py-1 text-xs font-medium text-navy-600">{tag}</span>
                    )}
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div className="rounded-2xl bg-white border border-navy-100 overflow-hidden shadow-sm hover:shadow-luxury transition-shadow duration-300">
                <div className="relative h-64 bg-gradient-to-br from-gold-500 to-gold-700 flex items-center justify-center">
                  <div className="h-24 w-24 rounded-full bg-white/15 border-2 border-white/30 flex items-center justify-center">
                    <span className="text-3xl font-display text-white">MK</span>
                  </div>
                  <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-white to-transparent" />
                </div>
                <div className="p-7 pt-3">
                  <p className="eyebrow mb-2">Chief Executive Officer</p>
                  <h3 className="text-xl font-semibold text-navy-800 tracking-tight">Manoj kumar</h3>
                  <p className="mt-3 text-sm text-navy-600/70 leading-relaxed">Manoj brings over two decades of deep property expertise spanning residential, commercial, and luxury real estate across India. His strategic vision drives AssetCare365's expansion and his extensive network of developers, legal experts, and institutional partners ensures every client receives premium-grade asset management.</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {["20+ Years Property Expert", "Real Estate Strategist", "Luxury Asset Specialist"].map((tag) =>
                    <span key={tag} className="rounded-full bg-gold-50 px-3 py-1 text-xs font-medium text-gold-700">{tag}</span>
                    )}
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="rounded-2xl bg-white border border-navy-100 overflow-hidden shadow-sm hover:shadow-luxury transition-shadow duration-300">
                <div className="relative h-64 bg-gradient-to-br from-emerald-600 to-emerald-800 flex items-center justify-center">
                  <div className="h-24 w-24 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center">
                    <span className="text-3xl font-display text-white">N</span>
                  </div>
                  <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-white to-transparent" />
                </div>
                <div className="p-7 pt-3">
                  <p className="eyebrow mb-2">Operations Head</p>
                  <h3 className="text-xl font-semibold text-navy-800 tracking-tight">Nikhil kumar</h3>
                  <p className="mt-3 text-sm text-navy-600/70 leading-relaxed">Nikhil brings hands on operational expertise to ensure every property under management receives the attention it deserves. His on ground leadership drives the transparency, responsiveness, and execution quality that defines AssetCare365.</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {["Operations Specialist", "On-Ground Expert", "Client Relations"].map((tag) =>
                    <span key={tag} className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">{tag}</span>
                    )}
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.4}>
              <div className="rounded-2xl bg-navy-800 text-white overflow-hidden noise-overlay h-full">
                <div className="p-8 h-full flex flex-col justify-between">
                  <div>
                    <p className="eyebrow text-emerald-300 mb-4">The Team</p>
                    <h3 className="text-xl font-semibold text-white tracking-tight">50+ Dedicated Professionals</h3>
                    <p className="mt-4 text-sm text-white/65 leading-relaxed">Our pan-India team includes field inspectors, property analysts, legal experts, maintenance coordinators, and client relationship managers — all committed to one goal: protecting your asset and your peace of mind.</p>
                  </div>
                  <div className="mt-8 grid grid-cols-2 gap-4">
                    <div className="rounded-xl bg-white/10 p-4"><p className="text-2xl font-semibold text-white">7</p><p className="text-xs text-white/50 mt-1">Cities Active</p></div>
                    <div className="rounded-xl bg-white/10 p-4"><p className="text-2xl font-semibold text-gold-300">24/7</p><p className="text-xs text-white/50 mt-1">Support Available</p></div>
                    <div className="rounded-xl bg-white/10 p-4 col-span-2">
                      <div className="flex items-center gap-2 mb-1"><MapPin size={14} className="text-emerald-400" /><p className="text-xs text-white/50">Coverage</p></div>
                      <p className="text-sm text-white/80">Delhi · Mumbai · Bengaluru · Hyderabad · Kolkata · Ahmedabad · Goa</p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-32">
        <div className="container-luxury">
          <SectionHeading eyebrow="Our Values" title="The Principles We" titleAccent="Operate By" />
          <div className="mt-16 grid gap-6 sm:grid-cols-2">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={i * 0.1}>
                  <div className="flex gap-5 rounded-2xl border border-navy-100 bg-white p-7 hover:shadow-luxury transition-shadow duration-300">
                    <div className="shrink-0 h-12 w-12 rounded-xl bg-navy-800 flex items-center justify-center text-gold-400"><Icon size={20} strokeWidth={1.5} /></div>
                    <div>
                      <h3 className="font-semibold text-navy-800 tracking-tight">{v.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-navy-600/70">{v.desc}</p>
                    </div>
                  </div>
                </Reveal>);

            })}
          </div>
        </div>
      </section>

      <CTABanner />
    </>);

}